import React from 'react';
import { X, Calendar, User, FileText, CheckCircle, Clock } from 'lucide-react';

const RequestDetailsModal = ({ isOpen, onClose, request, type = 'LEAVE' }) => {
  if (!isOpen || !request) return null;

  const employeeName = request.employeeId 
    ? `${request.employeeId.firstName || ''} ${request.employeeId.lastName || ''}`.trim()
    : 'Employee';
  const employeeCode = request.employeeId?.employeeId || '';

  const getDatesLabel = () => {
    if (type === 'LEAVE') {
      const start = request.startDate ? new Date(request.startDate).toLocaleDateString() : '-';
      const end = request.endDate ? new Date(request.endDate).toLocaleDateString() : '-';
      return start === end ? start : `${start} to ${end}`;
    }
    if (type === 'WFH') {
      const start = request.startDate ? new Date(request.startDate).toLocaleDateString() : '-';
      const end = request.endDate ? new Date(request.endDate).toLocaleDateString() : '-';
      return start === end ? start : `${start} to ${end}`;
    }
    return request.date ? new Date(request.date).toLocaleDateString() : '-';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'REJECTED': return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const title = type === 'LEAVE' ? 'Leave Request Details' : type === 'WFH' ? 'Work From Home Request Details' : 'On-Duty / Meeting Request Details';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-gray-100 overflow-hidden transform transition-all animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center border border-white/20">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">{title}</h3>
              <p className="text-xs text-gray-300 mt-0.5">Full Request Information & Reason</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Employee & Type */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-1">
                Employee
              </span>
              <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                <User className="w-4 h-4 text-gray-500 shrink-0" />
                <span className="truncate">{employeeName}</span>
              </p>
              {employeeCode && <span className="text-xs text-gray-500 ml-5.5">{employeeCode}</span>}
            </div>

            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-1">
                Request Type
              </span>
              <p className="text-sm font-bold text-gray-800">
                {type === 'LEAVE' ? (request.leaveType?.name || 'Leave') : type}
              </p>
              {type === 'LEAVE' && request.days && (
                <span className="text-xs text-gray-500">{request.days} Day(s)</span>
              )}
            </div>
          </div>

          {/* Date & Status */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-1">
                Requested Date(s)
              </span>
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
                <span>{getDatesLabel()}</span>
              </p>
            </div>

            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-1">
                Current Status
              </span>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadgeClass(request.status)}`}>
                <CheckCircle className="w-3.5 h-3.5" />
                {request.status || 'PENDING'}
              </span>
            </div>
          </div>

          {/* Full Reason Box */}
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">
              Full Reason / Details
            </span>
            <div className="p-4 bg-amber-50/50 rounded-2xl border border-amber-200/80 text-gray-800 text-sm leading-relaxed whitespace-pre-wrap break-words min-h-[90px] shadow-sm">
              {request.reason ? request.reason : (
                <span className="text-gray-400 italic">No reason provided.</span>
              )}
            </div>
          </div>

          {/* Approval Breakdown */}
          <div className="pt-2 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 gap-2 text-center text-xs">
            {request.hrStatus && (
              <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-gray-400 block text-[10px] font-bold uppercase">HR Status</span>
                <span className="font-bold text-gray-700 mt-0.5 inline-block">{request.hrStatus}</span>
              </div>
            )}
            {request.managerStatus && (
              <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-gray-400 block text-[10px] font-bold uppercase">Manager</span>
                <span className="font-bold text-gray-700 mt-0.5 inline-block">{request.managerStatus}</span>
              </div>
            )}
            {request.paymentStatus && (
              <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100 col-span-2 sm:col-span-1">
                <span className="text-gray-400 block text-[10px] font-bold uppercase">Pay Status</span>
                <span className="font-bold text-gray-700 mt-0.5 inline-block">{request.paymentStatus.replace(/_/g, ' ')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white text-sm font-semibold rounded-xl shadow transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsModal;
